import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';
import { createOptionsCache } from '../../../core/options/options-cache.js';
import { renderOptionContent } from '../../../core/options/render-option-content.js';

// Per-instance counter so the portal panel / listbox / option ids never collide
// across mounts (a field key is not unique across the lifetime of the app —
// mount→destroy→remount reuses the key). Referenced by aria-controls /
// aria-activedescendant, which must resolve to THIS instance's elements.
let instanceCounter = 0;

/** @type {import('../../../contracts/forms.js').FieldDriver} */
export const asyncSelectDriver = {
  type: 'async-select',
  mount(container, ctx) {
    const { def, onChange } = ctx;
    const asDef = /** @type {import('../../../contracts/forms.js').AsyncSelectFieldDef} */ (def);
    const uid = `async-select-${instanceCounter++}`;
    const panelId = `${uid}-panel`;
    const listboxId = `${uid}-listbox`;

    const debounceMs = asDef.debounceMs ?? 300;
    const allowClear = asDef.allowClear ?? false;

    let destroyed = false;
    let forcedDisabled = false;

    /** @type {unknown} */
    let currentValue = ctx.value !== undefined ? ctx.value : '';
    /** @type {string} */
    let displayLabel = currentValue == null || currentValue === '' ? '' : `#${currentValue}`;
    /** @type {boolean} */
    let isOpen = false;
    /** @type {number} */
    let highlightedIndex = -1;
    /** @type {string} */
    let currentIcon = '';
    /** @type {string} */
    let currentIconUrl = '';
    /** @type {import('../../../contracts/forms.js').FieldOption[]} */
    let currentOptions = [];

    // --- DOM ---
    // The wrapper is a plain layout container — no ARIA. The TRIGGER is the
    // value-displaying combobox (button IS labelable by <label for>, and
    // aria-required/aria-invalid are valid on role="combobox"). The portaled
    // panel is a role="dialog" whose inner search input is a SECOND combobox
    // owning the listbox (WAI-ARIA dual-combobox pattern; mirrors context-switcher).
    const wrapper = createElement('div', { class: 'async-select-driver' });

    const trigger = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button',
      class: 'async-select-driver__trigger',
      id: `field-${def.key}`, // matches <label for="field-{key}"> → accessible name
      role: 'combobox',
      'aria-haspopup': 'dialog',
      'aria-expanded': 'false',
      'aria-controls': panelId,
    }));
    if (def.required) trigger.setAttribute('aria-required', 'true');

    const displaySpan = createElement('span', { class: 'async-select-driver__value' });
    displaySpan.textContent = displayLabel || (def.placeholder ?? 'Select...');
    trigger.appendChild(displaySpan);

    if (allowClear) {
      const clearBtn = createElement('button', {
        type: 'button',
        class: 'async-select-driver__clear',
        'aria-label': 'Clear',
      }, ['×']);
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (trigger.disabled) return;
        currentValue = '';
        displayLabel = '';
        currentIcon = '';
        currentIconUrl = '';
        updateDisplay();
        // Close if open, then restore focus to the trigger. restoreFocusToTrigger
        // keeps it enabled through a pending load (flag suppresses the loading-disable)
        // and reconciles the flag with whether focus actually landed. Works whether or
        // not the dropdown was open (closeDropdown no-ops when closed).
        closeDropdown();
        restoreFocusToTrigger();
        onChange('');
      });
      wrapper.appendChild(clearBtn);
    }

    // Search input — the inner combobox owning the listbox. Kept `disabled` while
    // the dropdown is closed so a closed panel contributes ZERO focusables to a
    // parent modal's Tab-trap (the editor trap filters only [disabled]).
    const searchInput = /** @type {HTMLInputElement} */ (createElement('input', {
      type: 'text',
      class: 'async-select-driver__search',
      placeholder: 'Search...',
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': 'false',
      'aria-controls': listboxId,
      'aria-label': `Search ${def.label}`,
      autocomplete: 'off',
    }));
    searchInput.disabled = true;

    // Dropdown panel — a portaled dialog (search input + listbox).
    const panel = createElement('div', {
      class: 'async-select-driver__dropdown',
      role: 'dialog',
      'aria-label': String(def.label ?? ''),
      id: panelId,
    });
    panel.style.display = 'none';
    panel.appendChild(searchInput);

    const optionsList = createElement('ul', {
      class: 'async-select-driver__options',
      role: 'listbox',
      id: listboxId,
      'aria-label': `${def.label ?? ''} options`,
    });
    panel.appendChild(optionsList);

    // Portal into the nearest dialog ancestor (so the OPEN panel's search input
    // is a descendant of the editor's focus-trap scope) — else document.body for
    // non-modal consumers. position:fixed escapes overflow clipping either way.
    const portalHost = container.closest('[role="dialog"]') ?? document.body;
    portalHost.appendChild(panel);

    wrapper.appendChild(trigger);
    // Initial disabled state is set by syncTriggerDisabled() via the synchronous
    // first tick of subscribeWithInitial below — no separate mount-time write, so
    // syncTriggerDisabled stays the single owner (def.disabled/forcedDisabled apply).

    container.appendChild(wrapper);

    // --- Options provider (with TTL cache keyed by field key + query) ---
    const cache = createOptionsCache();
    const cacheNs = `async-select:${def.key}`;

    /** @type {import('../../../contracts/forms.js').AsyncSelectFieldDef['loadOptions']} */
    const cachedLoadOptions = asDef.loadOptions
      ? async (opts) => {
        const cacheKey = JSON.stringify({ q: opts.query, v: opts.values ?? {} });
        if (cache.has(cacheNs, cacheKey)) {
          return /** @type {any} */ (cache.get(cacheNs, cacheKey));
        }
        const result = await /** @type {NonNullable<typeof asDef.loadOptions>} */ (asDef.loadOptions)(opts);
        cache.set(cacheNs, cacheKey, result);
        return result;
      }
      : undefined;

    const provider = createOptionsProvider({
      loadOptions: cachedLoadOptions,
      mapOption: asDef.mapOption,
      debounceMs,
    });

    // True while the trigger holds focus that a close({restoreFocus}) just handed
    // back to it. A loading-disable would blur that focus to <body> (escaping a
    // parent modal), so `syncTriggerDisabled` suppresses the loading-disable while
    // this holds. On genuine blur, clear the flag AND immediately recompute the
    // disabled state (derived state recomputed from source, §3) — so a trigger that
    // was kept enabled only for the restored-focus window becomes loading-disabled
    // the instant focus leaves, without waiting for an unrelated later update.
    let triggerHasRestoredFocus = false;
    trigger.addEventListener('blur', () => {
      triggerHasRestoredFocus = false;
      syncTriggerDisabled();
    });

    // THE single owner of the trigger's disabled state — every disabled write goes
    // through here. Rules: forcedDisabled / def.disabled always win (the field is
    // genuinely disabled). Loading disables the trigger ONLY while the dropdown is
    // CLOSED — an open popup drives interaction through the search input — AND only
    // while the trigger doesn't currently own restored focus (disabling a focused
    // element drops focus to <body>, escaping a parent modal). That focus is transient: the next real blur/open
    // clears the flag and a later sync re-applies the loading-disable.
    function syncTriggerDisabled() {
      const loadingBlocks = !isOpen && !triggerHasRestoredFocus && provider.getState().loading;
      trigger.disabled = forcedDisabled || Boolean(def.disabled) || loadingBlocks;
    }

    // Restore focus to the trigger and keep the derived disabled-state honest. Set
    // the flag BEFORE syncing so a loading-disable is suppressed (a disabled element
    // can't take focus). Then focus, and RECONCILE with reality: the flag must record
    // SUCCESS, not intent — if focus didn't land (e.g. forced/def-disabled wins, or a
    // detached node), clear the flag and resync so it can't later wrongly suppress a
    // legitimate loading-disable.
    function restoreFocusToTrigger() {
      triggerHasRestoredFocus = true;
      syncTriggerDisabled();
      trigger.focus();
      if (document.activeElement !== trigger) {
        triggerHasRestoredFocus = false;
        syncTriggerDisabled();
      }
    }

    const unsubscribe = subscribeWithInitial(provider, (state) => {
      if (destroyed) return;
      syncTriggerDisabled();
      currentOptions = state.options.slice();
      // Re-sync icon + label when options arrive after setValue
      if (currentValue != null && currentValue !== '') {
        const match = currentOptions.find(o => o.value === String(currentValue ?? ''));
        if (match) {
          const nextIcon = match.icon || '';
          const nextIconUrl = match.iconUrl || '';
          const isPlaceholder = displayLabel === `#${currentValue}` || (displayLabel === '' && !currentIcon && !currentIconUrl);
          const labelDrifted = displayLabel !== match.label;
          const needsUpdate = nextIcon !== currentIcon || nextIconUrl !== currentIconUrl || isPlaceholder || labelDrifted;
          if (needsUpdate) {
            currentIcon = nextIcon;
            currentIconUrl = nextIconUrl;
            displayLabel = match.label;
            updateDisplay();
          }
        }
      }
      if (isOpen) {
        renderOptions();
      }
    });

    // --- Initial resolveLabel ---
    if (currentValue !== '' && currentValue != null && asDef.resolveLabel) {
      const requested = currentValue;
      asDef.resolveLabel(currentValue).then((label) => {
        // Drop a stale paint: bail if the value changed while the lookup was in flight.
        // Normalized compare — option values are matched by String(value) elsewhere, so a
        // number id vs its string form is the SAME selection, not stale.
        if (destroyed || String(requested ?? '') !== String(currentValue ?? '')) return;
        displayLabel = label;
        updateDisplay();
      }).catch(() => { /* ignore — keep #${value} placeholder */ });
    }

    // --- Eager options load for iconUrl hydration ---
    if (asDef.eagerLoadOptions && currentValue != null && currentValue !== '' && currentOptions.length === 0) {
      provider.load('', ctx.getFormValues?.() ?? {});
    }

    // --- Functions ---
    function updateDisplay() {
      if (currentValue != null && currentValue !== '') {
        const label = displayLabel || (currentIcon || currentIconUrl ? '' : String(currentValue));
        renderOptionContent(
          { label, value: String(currentValue), icon: currentIcon, iconUrl: currentIconUrl },
          displaySpan,
        );
      } else {
        displaySpan.textContent = def.placeholder ?? 'Select...';
      }
    }

    function positionPanel() {
      const rect = wrapper.getBoundingClientRect();
      panel.style.position = 'fixed';
      panel.style.left = `${rect.left}px`;
      panel.style.top = `${rect.bottom}px`;
      panel.style.minWidth = `${rect.width}px`;
      panel.style.zIndex = '9999';
    }

    function renderOptions() {
      optionsList.textContent = '';
      highlightedIndex = -1;
      // Rerender drops all option ids → clear a now-stale activedescendant.
      searchInput.removeAttribute('aria-activedescendant');
      currentOptions.forEach((opt, idx) => {
        const li = createElement('li', {
          role: 'option',
          class: 'async-select-driver__option',
          'aria-selected': opt.value === String(currentValue ?? '') ? 'true' : 'false',
          id: `${uid}-opt-${idx}`,
        });
        renderOptionContent(opt, li);
        li.addEventListener('mousedown', (e) => {
          e.preventDefault(); // prevent blur on the search input
          selectOption(opt);
        });
        li.addEventListener('mouseover', () => {
          setHighlight(idx);
        });
        optionsList.appendChild(li);
      });
    }

    /** @param {number} idx */
    function setHighlight(idx) {
      const items = optionsList.querySelectorAll('.async-select-driver__option');
      items.forEach((item, i) => {
        item.classList.toggle('async-select-driver__option--highlighted', i === idx);
      });
      highlightedIndex = idx;
      if (idx >= 0 && items[idx]) {
        searchInput.setAttribute('aria-activedescendant', items[idx].id || '');
      } else {
        searchInput.removeAttribute('aria-activedescendant');
      }
    }

    /** @param {import('../../../contracts/forms.js').FieldOption} opt */
    function selectOption(opt) {
      currentValue = opt.value;
      displayLabel = opt.label;
      currentIcon = opt.icon || '';
      currentIconUrl = opt.iconUrl || '';
      updateDisplay();
      closeDropdown({ restoreFocus: true }); // committed → focus back on the trigger (shows new value)
      onChange(opt.value);
    }

    function openDropdown() {
      if (isOpen || trigger.disabled) return;
      isOpen = true;
      // Re-enable the trigger for the open lifetime (the load kicked off below would
      // otherwise loading-disable it and break Escape's focus-restore). syncTriggerDisabled
      // keeps forcedDisabled/def.disabled authoritative.
      syncTriggerDisabled();
      trigger.setAttribute('aria-expanded', 'true');
      searchInput.setAttribute('aria-expanded', 'true');
      searchInput.disabled = false; // enable BEFORE focusing (a disabled input can't take focus)
      panel.style.display = '';
      positionPanel();
      renderOptions();
      searchInput.value = '';
      provider.load('', ctx.getFormValues?.() ?? {});
      setTimeout(() => searchInput.focus(), 0);
    }

    /**
     * @param {{ restoreFocus?: boolean }} [opts] - restoreFocus:true moves focus back
     *   to the trigger (Escape / selection / trigger-toggle); false or omitted leaves
     *   focus where it is (focusout / Tab-away / outside-click / destroy). Note: focus
     *   is only moved when the dropdown was actually open (early-return otherwise) —
     *   callers that must restore focus regardless of open state focus the trigger
     *   themselves (see the clear button).
     */
    function closeDropdown(opts) {
      if (!isOpen) return;
      isOpen = false;
      trigger.setAttribute('aria-expanded', 'false');
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.removeAttribute('aria-activedescendant');
      panel.style.display = 'none';
      searchInput.disabled = true; // re-exclude from a parent modal's Tab-trap
      if (opts?.restoreFocus) {
        restoreFocusToTrigger();
      } else {
        // Closed by focus-leave/outside/destroy — recompute the closed-state disabled
        // rule (loading disables again now the trigger holds no restored focus).
        syncTriggerDisabled();
      }
    }

    // --- Events ---
    trigger.addEventListener('click', () => {
      if (isOpen) {
        closeDropdown({ restoreFocus: true }); // toggle-close via the trigger → focus stays on it
      } else {
        openDropdown();
      }
    });

    trigger.addEventListener('focus', () => {
      // Don't auto-open on focus — only open on explicit click or keyboard
    });

    searchInput.addEventListener('input', () => {
      provider.load(searchInput.value, ctx.getFormValues?.() ?? {});
    });

    // Focus leaving the panel closes it WITHOUT restoring focus (the user Tabbed
    // away / focus moved elsewhere — don't yank it back to the trigger). Focus
    // moving to anything INSIDE the panel or the field wrapper is NOT a real leave:
    // an option mousedown (stays in the panel), and — critically — a real pointer
    // click on the CLEAR button (which lives in the wrapper, not the panel). In a
    // browser, the clear mousedown moves focus first, firing this focusout with the
    // clear button as relatedTarget; treating that as a leave would close+loading-
    // disable the trigger before the clear click runs, aborting the clear.
    panel.addEventListener('focusout', (e) => {
      const next = /** @type {Node | null} */ (/** @type {FocusEvent} */ (e).relatedTarget);
      if (next && (panel.contains(next) || wrapper.contains(next))) return;
      closeDropdown({ restoreFocus: false });
    });

    /**
     * Shared keyboard handler. Attached to the trigger (closed state) and the
     * searchInput (open state — focus moves there after open). While OPEN, handled
     * keys stopPropagation so they don't reach the crud-editor's document-level
     * Escape/Tab listener (async-select lives inside that modal).
     * @param {KeyboardEvent} e
     */
    function handleKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
        } else {
          e.stopPropagation();
          setHighlight(Math.min(highlightedIndex + 1, currentOptions.length - 1));
        }
      } else if (e.key === 'ArrowUp') {
        // Closed: truly untouched (no preventDefault, no highlight, no activedescendant).
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setHighlight(Math.max(highlightedIndex - 1, 0));
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
        } else {
          e.stopPropagation();
          if (highlightedIndex >= 0 && currentOptions[highlightedIndex]) {
            selectOption(currentOptions[highlightedIndex]);
          }
        }
      } else if (e.key === 'Escape') {
        // Contain ONLY when open — a closed Escape must bubble so the editor closes.
        const wasOpen = isOpen;
        if (wasOpen) {
          e.preventDefault();
          e.stopPropagation();
          closeDropdown({ restoreFocus: true });
        }
      }
    }

    trigger.addEventListener('keydown', handleKeydown);
    searchInput.addEventListener('keydown', handleKeydown);

    // Outside click — close without restoring focus (the user clicked elsewhere).
    /** @param {MouseEvent} e */
    function handleDocumentMousedown(e) {
      if (destroyed || !isOpen) return;
      const target = /** @type {Node} */ (e.target);
      if (!wrapper.contains(target) && !panel.contains(target)) {
        closeDropdown({ restoreFocus: false });
      }
    }
    document.addEventListener('mousedown', handleDocumentMousedown);

    // Reposition on scroll/resize
    function handleReposition() {
      if (isOpen) positionPanel();
    }
    window.addEventListener('scroll', handleReposition, { passive: true, capture: true });
    window.addEventListener('resize', handleReposition, { passive: true });

    return {
      setValue(value) {
        currentValue = value;
        const opt = currentOptions.find(o => o.value === String(value ?? ''));
        if (opt) {
          displayLabel = opt.label;
          currentIcon = opt.icon || '';
          currentIconUrl = opt.iconUrl || '';
        } else if (value != null && value !== '') {
          displayLabel = `#${value}`;
          currentIcon = '';
          currentIconUrl = '';
          if (asDef.resolveLabel) {
            const requested = value;
            asDef.resolveLabel(value).then((label) => {
              // Drop a stale paint: a slower lookup for an old value must not repaint over a
              // newer setValue. Normalized compare (number id vs string id is the same value).
              if (destroyed || String(requested ?? '') !== String(currentValue ?? '')) return;
              displayLabel = label;
              updateDisplay();
            }).catch(() => {});
          }
          // Eager load to hydrate iconUrl when options aren't cached yet
          if (asDef.eagerLoadOptions && currentOptions.length === 0 && !provider.getState().loading) {
            provider.load('', ctx.getFormValues?.() ?? {});
          }
        } else {
          displayLabel = '';
          currentIcon = '';
          currentIconUrl = '';
        }
        updateDisplay();
      },
      getValue() {
        return currentValue;
      },
      setErrors() {
        // errors rendered by host
      },
      destroy() {
        destroyed = true;
        unsubscribe();
        provider.destroy();
        cache.destroy();
        closeDropdown({ restoreFocus: false }); // never focus the about-to-be-removed trigger
        document.removeEventListener('mousedown', handleDocumentMousedown);
        window.removeEventListener('scroll', handleReposition, /** @type {any} */ ({ capture: true }));
        window.removeEventListener('resize', handleReposition);
        panel.remove();
        wrapper.remove();
      },
      setDisabled(disabled) {
        forcedDisabled = disabled;
        syncTriggerDisabled();
      },
      clearValue() {
        currentValue = '';
        displayLabel = '';
        currentIcon = '';
        currentIconUrl = '';
        updateDisplay();
        // No onChange emission — silent host-driven reset
      },
      getOptionsProvider() {
        return provider;
      },
      setAriaInvalid(invalid) {
        // Targets the trigger — the labelable combobox that owns the field's ARIA state.
        trigger.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      },
    };
  },
};
